/**
 * Spot Gamma, Gamma Exposure (GEX), and MMEC Gamma — Stable/Theta data only.
 *
 * Values are read from Stable/Theta fields via getStableFieldValue. When Gamma is missing
 * or effectively zero, Black–Scholes (and Vega-derived gamma when applicable) is standard fallback.
 *
 * Formulas (retail / SpotGamma / SqueezeMetrics style):
 *
 * 1) Per-contract spot gamma (dollar gamma per 1% move; uses OI — not the same as GEX):
 *    spotGamma = gamma × OI × S² × 0.01 × sign   (sign = +1 call, -1 put)
 *
 * 2) Gamma exposure (GEX) — separate metric:
 *    gex = gamma × OI × 100 × S × sign × 0.01
 *
 * 3) MMEC Gamma:
 *    mmeGamma = gamma × delta × S² × 0.01 × sign
 *
 * All inputs: Gamma, Open Interest, Underlying Price, Delta, Option Type from Stable/Theta only.
 */
import { DataMode } from '../types/DataMode';
import { getStableFieldValue } from './dataModeUtils';

const CONTRACT_SIZE = 100;
const DEFAULT_IV = 0.2;

export interface GammaResult {
  spotGamma: number;
  gex: number;
  volumeGex: number;
  mmeGamma: number;
  gamma: number;
  openInterest: number;
  volume: number;
  underlyingPrice: number;
  direction: number;
}

function isCall(item: any): boolean {
  const t = String(item['Option Type'] ?? item.optionType ?? '').toUpperCase();
  return t === 'C' || t === 'CALL';
}

/** Parse Stable option symbol (e.g. SPY260206C00671000) to expiry YYYY-MM-DD. */
function parseExpiryFromSymbol(symbol: string): string {
  if (!symbol || symbol.length < 15) return '';
  const m = symbol.match(/(\d{6})([CP])(\d{8})$/);
  if (!m) return '';
  const yy = m[1].slice(0, 2);
  const mm = m[1].slice(2, 4);
  const dd = m[1].slice(4, 6);
  return `20${yy}-${mm}-${dd}`;
}

/** Expiry string (YYYY-MM-DD) to years from now. Returns 0 if invalid. */
function timeToExpiryYears(expiryStr: string): number {
  if (!expiryStr || typeof expiryStr !== 'string') return 0;
  const s = expiryStr.trim();
  if (s.length < 10) return 0;
  try {
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(5, 7), 10);
    const d = parseInt(s.slice(8, 10), 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return 0;
    const expiry = new Date(y, m - 1, d);
    const now = new Date();
    const days = (expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
    if (days < 0) return 0;
    return Math.max(days / 365.25, 1 / 365.25);
  } catch {
    return 0;
  }
}

/** Black–Scholes gamma = n(d1) / (S * sigma * sqrt(T)). Returns 0 if invalid. */
function bsGamma(S: number, K: number, T: number, sigma: number): number {
  if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) return 0;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (0.05 + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const nD1 = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);
  return nD1 / (S * sigma * sqrtT);
}

/** Default T (years) when expiry is missing so BS fallback still produces non-zero gamma. */
const DEFAULT_T_YEARS = 30 / 365.25;

/** Read a numeric Greek from item (Vega, Theta, IV) from common keys. */
function readGreek(item: any, keys: string[]): number {
  for (const k of keys) {
    const v = item?.[k];
    if (v === undefined || v === null || v === '' || v === '-') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/**
 * Derive gamma from Vega when Gamma is missing.
 * BS relationship: vega (per 1% vol) = S * n(d1) * sqrt(T) / 100, gamma = n(d1)/(S*σ*sqrt(T))
 * => gamma = vega * 100 / (S² * σ * T). Returns 0 if invalid.
 */
function gammaFromVega(S: number, vega: number, sigma: number, T: number): number {
  if (S <= 0 || vega <= 0 || sigma <= 0 || T <= 0) return 0;
  const den = S * S * sigma * T;
  if (den <= 0) return 0;
  return (vega * 100) / den;
}

/** Gamma for one option: use feed gamma when meaningful; else Vega→gamma; else Black–Scholes. */
function getStableGamma(item: any, underlyingPriceFallback?: number): number {
  const raw = getStableFieldValue(item, 'Gamma');
  if (raw > 1e-12) return raw;

  let S = getStableFieldValue(item, 'Underlying Price') || 0;
  if (S <= 0 && underlyingPriceFallback != null && underlyingPriceFallback > 0) S = underlyingPriceFallback;
  const K = Number(item?.Strike ?? item?.['Strike Price'] ?? item?.strike ?? 0) || 0;
  if (S <= 0 || K <= 0) return 0;

  const expiry = (item?.['Expiry Date'] ?? item?.expiry_date ?? parseExpiryFromSymbol(String(item?.Symbol ?? item?.symbol ?? ''))).trim();
  let T = timeToExpiryYears(expiry);
  if (T <= 0) T = DEFAULT_T_YEARS;

  const ivRaw = item?.['Implied Volatility'] ?? item?.iv ?? item?.implied_volatility;
  let sigma = DEFAULT_IV;
  if (ivRaw != null && ivRaw !== '' && ivRaw !== '-') {
    const iv = typeof ivRaw === 'number' ? ivRaw : parseFloat(String(ivRaw).replace(/,/g, ''));
    if (Number.isFinite(iv) && iv > 0) sigma = iv < 2 ? iv : iv / 100;
  }

  const vega = readGreek(item, ['Vega', 'vega']);
  if (vega > 1e-12) {
    const fromVega = gammaFromVega(S, vega, sigma, T);
    if (fromVega > 1e-12) return fromVega;
  }

  return bsGamma(S, K, T, sigma);
}

/**
 * Compute spot gamma, GEX, and MMEC gamma for one option row using Stable/Theta data only.
 * Gamma, Open Interest, Underlying Price, Delta, Option Type all from getStableFieldValue.
 */
export function computeSpotGammaGexMme(
  item: any,
  _dataMode: DataMode,
  underlyingPriceFallback?: number
): GammaResult {
  const gamma = getStableGamma(item, underlyingPriceFallback);
  const openInterest = getStableFieldValue(item, 'Open Interest');
  const volume = getStableFieldValue(item, 'Volume');
  const rowUnderlying = getStableFieldValue(item, 'Underlying Price');
  const S =
    rowUnderlying > 0
      ? rowUnderlying
      : Number.isFinite(underlyingPriceFallback) && underlyingPriceFallback! > 0
        ? underlyingPriceFallback!
        : 100;
  const sign = isCall(item) ? 1 : -1;

  // Total spot gamma for this option = Gamma × OI × S² × 0.01 × sign (aggregate by strike for net)
  const spotGamma = gamma * openInterest * S * S * 0.01 * sign;
  const gex = gamma * openInterest * CONTRACT_SIZE * S * sign * 0.01;
  // Volume gamma exposure: same formula as GEX but using Volume instead of OI
  const volumeGex = gamma * volume * CONTRACT_SIZE * S * sign * 0.01;
  const deltaAskBid = getStableFieldValue(item, 'Delta');
  const mmeGamma = gamma * deltaAskBid * S * S * 0.01 * sign;

  return {
    spotGamma,
    gex,
    volumeGex,
    mmeGamma,
    gamma,
    openInterest,
    volume,
    underlyingPrice: S,
    direction: sign
  };
}

/**
 * Convert raw spot gamma (dollar gamma per 1%) to competitor-style display index.
 * So charts show values like 33, 32, 13, -7.1 that match SpotGamma/similar platforms.
 * Formula: display = spotGamma / (0.0945 * S²). For SPY S≈690 this yields divisor ~45000.
 */
export function spotGammaToDisplayIndex(spotGamma: number, underlyingPrice: number): number {
  if (underlyingPrice <= 0 || !Number.isFinite(spotGamma)) return spotGamma;
  const divisor = 0.0945 * underlyingPrice * underlyingPrice;
  return divisor !== 0 ? spotGamma / divisor : spotGamma;
}

/**
 * Get a stable underlying price from the first row of a dataset (for use as fallback).
 * Tries first few rows and common keys so gamma is computable even when keys vary.
 */
export function getUnderlyingPriceFallback(data: any[], _dataMode: DataMode): number {
  if (!data?.length) return 100;
  for (let i = 0; i < Math.min(5, data.length); i++) {
    const v = getStableFieldValue(data[i], 'Underlying Price');
    if (v > 0) return v;
    const raw = data[i]?.['Underlying Price'] ?? data[i]?.UnderlyingPrice ?? data[i]?.underlying_price ?? data[i]?.Underlying_Price;
    if (raw != null && raw !== '' && raw !== '-') {
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return 100;
}
